/**
 * Admin Users Tab Component
 * V6.0 - Stripped analytics, added activity log + delete admin
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';
import { AdminUser } from '../../types/auth';
import apiService from '../../services/api';
import AddUserModal from './AddUserModal';
import UserTable from './UserTable';
import AdminActivityLog from './components/AdminActivityLog';
import { hasFullAccess, isSuperAdminEmail } from '../../config/permissionResources';

export const AdminUsersTab: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user: currentUser } = useSelector((state: RootState) => state.auth);

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const loadUsers = async () => {
    try {
      const response = await apiService.getAdminUsers();
      const loadedUsers: AdminUser[] = response.users || [];
      setUsers(loadedUsers);
    } catch (error) {
      if (error instanceof Error && error.message === 'Unauthorized') return;
      dispatch(addNotification({ type: 'error', message: error instanceof Error ? error.message : 'Failed to load users' }));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadUsers();
    setRefreshing(false);
    dispatch(addNotification({ type: 'success', message: 'Admin data refreshed' }));
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const canAddUsers =
    isSuperAdminEmail(currentUser?.email) ||
    hasFullAccess(currentUser?.permissions || [], 'admin_users', currentUser?.email);

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mb: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Admin User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage admin dashboard users and permissions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          {canAddUsers && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddUserModalOpen(true)}>
              Add Admin User
            </Button>
          )}
        </Box>
      </Box>

      {/* Permission Alert */}
      {!canAddUsers && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You need Admin Users (Full Access) permission to add new users. Contact your administrator for assistance.
        </Alert>
      )}

      {/* User Table */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Admin Users</Typography>
          <Typography variant="body2" color="text.secondary">
            Click "Edit Permissions" in the action menu to manage individual user permissions
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <UserTable users={users} onRefresh={loadUsers} currentUserId={currentUser?.id} />
      </Paper>

      {/* Admin Activity Log */}
      <AdminActivityLog />

      {/* Add User Modal */}
      <AddUserModal open={addUserModalOpen} onClose={() => setAddUserModalOpen(false)} onSuccess={loadUsers} />
    </Box>
  );
};

export default AdminUsersTab;
